/**
 * WebSocket Types and Interfaces
 * Based on backend/docs/websocket-api-specification.md
 */

// Base WebSocket Message Structure
export interface WebSocketMessage {
  type: string
  timestamp: number
  organization_id?: number
  project_id?: number
  data?: any
  error?: any
}

// Sync Status Update Payload
export interface SyncStatusUpdatePayload {
  project_id: number
  organization_id: number
  status: 'synced' | 'syncing' | 'error' | 'pending'
  last_sync: string
  forms_count: number
  project_name: string
  server_id: number
  cached_at: string
  sync_cursors: SyncCursor[]
}

// Form Status Change Payload
export interface FormStatusChangePayload {
  cursor_id: number
  form_id: string
  form_name: string
  old_status: string
  new_status: string
  is_active: boolean
  submissions_count: number
  last_sync: string
  sync_duration?: number
}

// Scheduler Status Update Payload
export interface SchedulerStatusUpdatePayload {
  scheduler_enabled: boolean
  last_run: string
  next_run: string
  active_jobs: number
  total_jobs: number
  status: 'running' | 'paused' | 'stopped'
  jobs?: Array<{
    job_id: string
    project_id: number
    status: string
    started_at: string
    progress: number
  }>
}

// Sync Complete Payload
export interface SyncCompletePayload {
  project_id: number
  project_name: string
  form_id: string
  form_name: string
  submissions_synced: number
  total_submissions: number
  sync_duration: number
  status: 'completed'
}

// Sync Error Payload
export interface SyncErrorPayload {
  project_id: number
  project_name: string
  form_id: string
  form_name: string
  error_type: string
  error_message: string
  retry_count: number
  max_retries: number
  will_retry: boolean
}

// Project Status Summary Payload
export interface ProjectStatusSummaryPayload {
  total_projects: number
  synced_projects: number
  syncing_projects: number
  error_projects: number
  total_forms: number
  synced_forms: number
  syncing_forms: number
  error_forms: number
  last_updated: string
}

// QC Validation Complete Payload
export interface QcValidationCompletePayload {
  instance_id: string
  project_id: number
  form_id: string
  validation_id: string
  total_rules: number
  passed_rules: number
  failed_rules: number
  warning_rules: number
  error_rules: number
  processing_time_ms: number
  results: QcRuleResult[]
}

// Connection Status Payload
export interface ConnectionStatusPayload {
  status: 'connected' | 'disconnected' | 'reconnecting'
  organization_id: number
  user_id: number
  connection_id: string
  connected_at: string
  heartbeat_interval: number
  max_connections_per_org: number
  current_connections: number
}

// Data Streaming Payloads
export interface DataStreamStartPayload {
  stream_id: string
  data_type: string
  total_chunks: number
  chunk_size: number
  total_items: number
}

export interface DataStreamChunkPayload {
  stream_id: string
  chunk_number: number
  chunk_data: any[]
  is_last: boolean
  items_in_chunk: number
}

export interface DataStreamEndPayload {
  stream_id: string
  total_chunks: number
  total_items: number
  status: 'completed' | 'failed' | 'cancelled'
}

// WebSocket Error Codes
export enum WebSocketErrorCode {
  NORMAL_CLOSURE = 1000,
  GOING_AWAY = 1001,
  PROTOCOL_ERROR = 1002,
  UNSUPPORTED_DATA = 1003,
  ABNORMAL_CLOSURE = 1006,
  INVALID_FRAME_PAYLOAD_DATA = 1007,
  POLICY_VIOLATION = 1008,
  MESSAGE_TOO_BIG = 1009,
  MANDATORY_EXTENSION = 1010,
  INTERNAL_ERROR = 1011,
  SERVICE_RESTART = 1012,
  TRY_AGAIN_LATER = 1013,
}

// WebSocket Connection State
export enum WebSocketState {
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3,
}

// WebSocket Options
export interface WebSocketOptions {
  organizationId: number
  token: string
  heartbeatIntervalMs?: number
  maxReconnectAttempts?: number
  reconnectDelayMs?: number
  onMessage?: (message: WebSocketMessage) => void
  onConnect?: () => void
  onDisconnect?: (code: number, reason: string) => void
  onError?: (error: Event) => void
}

// Re-export SyncCursor type from store
export interface SyncCursor {
  id: number
  organization_id: number
  server_id: number
  project_id: number
  odk_project_id: number
  xml_form_id: string
  last_updated_at?: string
  last_submission_date?: string
  last_sync_at?: string
  sync_status: string
  error_message?: string
  retry_count: number
  is_active: boolean
  created_at: string
  updated_at: string
  form_name?: string
  form_state?: 'open' | 'closing' | 'closed' | 'unknown'
  project_name?: string
}

// Re-export QcRuleResult type from store
export interface QcRuleResult {
  id: number
  instance_id: string
  rule_id: number
  rule_name: string
  rule_type: string
  field_path: string
  expected_value?: any
  actual_value?: any
  passed: boolean
  error_message?: string
  severity_level: 'error' | 'warning' | 'info'
  created_at: string
}
